import { useCallback, useRef, useState } from 'react';
import { SoundOutlined, TranslationOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { LiveTranscriptTurn } from '@/services/webchat-service/webchat.service.types';
import { getTranslationAudioUrl } from '@/services/webchat-service/webchat.service';
import { useWebchatStore } from '@/store/webchat.store';
import dayjs from 'dayjs';
import clsx from 'clsx';
import styles from './live-transcript-bubble.module.scss';

// True when the detected source language is something other than English
// (en, en-us, en-gb, etc). We hide the chip for English turns to avoid
// visual noise — most of the time the call is in English and the chip is
// only useful as a "this caller is speaking Polish" signal.
const isNonEnglishLanguage = (lang: string | null): boolean => {
  if (!lang) return false;
  return !lang.toLowerCase().startsWith('en');
};

// The 2-letter base of a BCP-47 code, used as the chip label. "pl-pl" -> "PL".
const formatLanguageChip = (lang: string): string => lang.split('-')[0].toUpperCase();

interface TurnAudioButtonProps {
  s3Key: string;
  kind: 'original' | 'translation';
  icon: React.ReactNode;
}

const AUDIO_LABEL_KEYS = {
  original: {
    play: 'liveTranscript.audio.playOriginal',
    stop: 'liveTranscript.audio.stopOriginal',
  },
  translation: {
    play: 'liveTranscript.audio.playTranslation',
    stop: 'liveTranscript.audio.stopTranslation',
  },
} as const satisfies Record<string, Record<'play' | 'stop', ParseKeys<'webchat'>>>;

function TurnAudioButton({ s3Key, kind, icon }: TurnAudioButtonProps) {
  const { t } = useTranslation('webchat');
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const toggle = useCallback(async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }

    if (!urlRef.current) {
      setLoading(true);
      try {
        const result = await getTranslationAudioUrl(s3Key);
        urlRef.current = result.url;
      } catch {
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    const audio = new Audio(urlRef.current!);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.play();
    setPlaying(true);
  }, [s3Key, playing]);

  return (
    <Tooltip title={t(playing ? AUDIO_LABEL_KEYS[kind].stop : AUDIO_LABEL_KEYS[kind].play)}>
      <button
        className={clsx(styles.audioButton, playing && styles.audioButtonActive)}
        onClick={toggle}
        disabled={loading}
        aria-label={t(AUDIO_LABEL_KEYS[kind].play)}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

// Renders a single live transcript turn from an active AI call. Visually
// mirrors ChatBubble: caller (user role) on the right, agent on the left.
//
// By default the bubble shows `translated_text` (English), with a small
// language chip (e.g. "PL") next to non-English turns so the rep knows what
// the caller is actually speaking. The webchat store carries a per-rep
// `showOriginalTranscript` toggle that flips every bubble in the panel back
// to the original-language text in one click.
// Maps the close-reason recorded on the bridge_end turn to a short human
// label. Reasons come from ElevenLabsCall#close_webchat! and the LLM bridge
// (`bridge_ended` is the LLM signalling both streams ended cleanly).
const BRIDGE_END_REASON_KEYS: Record<string, ParseKeys<'webchat'>> = {
  closed_by_agent: 'liveTranscript.bridgeEnd.reasons.closedByAgent',
  closed_by_visitor: 'liveTranscript.bridgeEnd.reasons.closedByVisitor',
  bridge_ended: 'liveTranscript.bridgeEnd.reasons.bridgeEnded',
  caller_hung_up_during_claim: 'liveTranscript.bridgeEnd.reasons.callerHungUpDuringClaim',
};

export const LiveTranscriptBubble = ({ turn }: { turn: LiveTranscriptTurn }) => {
  const { t } = useTranslation('webchat');
  const showOriginal = useWebchatStore((state) => state.showOriginalTranscript);

  const time = dayjs(turn.received_at).format('HH:mm:ss');

  // Synthetic divider for the end of the human-led portion. Skip the bubble
  // shape entirely; render a thin centred row so the agent can see exactly
  // where the bridge stopped.
  if (turn.type === 'bridge_end') {
    const reasonKey =
      BRIDGE_END_REASON_KEYS[turn.reason ?? ''] ?? 'liveTranscript.bridgeEnd.reasons.bridgeEnded';
    return (
      <div className={styles.bridgeEndDivider}>
        <span className={styles.bridgeEndLabel}>
          {t('liveTranscript.bridgeEnd.divider', { reason: t(reasonKey), time })}
        </span>
      </div>
    );
  }

  const side = turn.role === 'user' ? 'caller' : 'agent';
  const isBridgeTurn = turn.type === 'translation';
  // Bridge turns come from a human agent, not the AI; ElevenLabs turns come from the AI agent.
  const senderLabel =
    turn.role === 'user'
      ? t('liveTranscript.sender.caller')
      : isBridgeTurn
        ? t('liveTranscript.sender.agent')
        : t('liveTranscript.sender.aiAgent');
  const isCorrection = turn.type === 'agent_response_correction';

  // Field semantics differ between ElevenLabs AI turns and bridge translation turns:
  //
  // ElevenLabs AI turns  — text=original-language, translated_text=English
  //   default: show translated_text (English); showOriginal: show text (e.g. Polish)
  //
  // Bridge translation turns (type='translation'):
  //   agent turn  — text=English (what agent said), translated_text=visitor-language (what visitor heard)
  //     default: show text (English); showOriginal: show translated_text (visitor-language)
  //   visitor turn — text=English (translation), translated_text=English (same), original_text=caller-language
  //     default: show translated_text (English); showOriginal: show original_text (caller-language)
  let displayText: string | undefined;
  if (isBridgeTurn) {
    if (turn.role === 'agent') {
      displayText = showOriginal ? (turn.translated_text ?? turn.text) : turn.text;
    } else {
      displayText = showOriginal ? (turn.original_text ?? turn.text) : (turn.translated_text ?? turn.text);
    }
  } else {
    displayText = showOriginal ? turn.text : (turn.translated_text ?? turn.text);
  }

  // For bridge agent turns in showOriginal mode we display the visitor-language text,
  // so the chip should reflect the translated (visitor) language, not the agent's language.
  const chipLanguage = isBridgeTurn && turn.role === 'agent' && showOriginal
    ? turn.translated_language
    : turn.original_language;
  const showChip = isNonEnglishLanguage(chipLanguage ?? null);
  const hasAudio = turn.original_audio_s3_key || turn.translated_audio_s3_key;

  return (
    <div
      className={clsx(styles.bubbleWrapper, styles[side])}
      data-testid="live-transcript-bubble"
      data-role={turn.role}
    >
      <span className={styles.senderLabel}>
        {senderLabel}
        {isCorrection && <span className={styles.correctionTag}> {t('liveTranscript.correction')}</span>}
        {showChip && chipLanguage && (
          <span className={styles.languageChip}>{formatLanguageChip(chipLanguage)}</span>
        )}
      </span>
      <div className={clsx(styles.bubble, styles[side])}>{displayText}</div>
      {hasAudio && (
        <div className={styles.audioControls}>
          {turn.original_audio_s3_key && (
            <TurnAudioButton s3Key={turn.original_audio_s3_key} kind="original" icon={<SoundOutlined />} />
          )}
          {turn.translated_audio_s3_key && (
            <TurnAudioButton s3Key={turn.translated_audio_s3_key} kind="translation" icon={<TranslationOutlined />} />
          )}
        </div>
      )}
      <span className={styles.timestamp}>{time}</span>
    </div>
  );
};
